import { useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { colors, font } from 'plume-ui'
import { Round } from '../../../types'
import {
  SearchConditions,
  Sort,
  Order,
  getRoundsDebounced,
  getFundingRoundsDebounced,
  ABORTED,
} from '@/lib/api/round'
import { KeywordBox } from './molecules/KeywordBox'
import { InitialSeriesSelector } from './molecules/InitialSeriesSelector'
import { FundingAmountFilter } from './molecules/FundingAmountFilter'
import { PreValuationFilter } from './molecules/PreValuationFilter'
import { PostValuationFilter } from './molecules/PostValuationFilter'
import { TermFilter } from './molecules/TermFilter'
import { LoadingOverlay } from '../../atoms/LoadingOverlay'
import { FundingRoundData } from '../../../types/api'
import { RoundsInitialCondition, ViewMode } from '../../../pages/rounds'
import { ViewModeSelector } from './molecules/ViewModeSelector'
import { printError } from '@/lib/log'
import { useDebounce } from '../../../hooks/useDebounce'

export type OpeningCondition =
  | 'InitialSeries'
  | 'Term'
  | 'FundingAmount'
  | 'PreValuation'
  | 'PostValuation'
  | null

export type Term = {
  from: string | null
  to: string | null
}

const ITEM_PER_PAGE = 50

export const SearchConditionList: React.FC<{
  setRounds: React.Dispatch<React.SetStateAction<Round[]>>
  setFundingRounds: React.Dispatch<React.SetStateAction<FundingRoundData[]>>
  setCount: React.Dispatch<React.SetStateAction<number | null>>
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  openingCondition: OpeningCondition
  setOpeningCondition: React.Dispatch<React.SetStateAction<OpeningCondition>>
  sort: Sort
  order: Order
  view: ViewMode
  setView: React.Dispatch<React.SetStateAction<ViewMode>>
  initialCondition: RoundsInitialCondition
  setSearchConditions: React.Dispatch<React.SetStateAction<SearchConditions>>
}> = (props) => {
  const [searchCondition, setSearchCondition] = useState<SearchConditions>({
    keywords: props.initialCondition.keywords ?? '',
    initialSeries: props.initialCondition.initialSeries ?? '',
    limit: ITEM_PER_PAGE,
    offset: 0,
  })
  const [keywords, setKeywords] = useState(
    props.initialCondition.keywords ?? ''
  )
  const debouncedKeywords = useDebounce(keywords, 500)
  const [selectedInitialSeries, setSelectedInitialSeries] = useState(
    props.initialCondition.initialSeries ?? ''
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [fundingAmountFrom, setFundingAmountFrom] = useState<number | null>(
    props.initialCondition.amountFrom ?? null
  )
  const [fundingAmountTo, setFundingAmountTo] = useState<number | null>(
    props.initialCondition.amountTo ?? null
  )
  const debouncedFundingAmountFrom = useDebounce(fundingAmountFrom, 500)
  const debouncedFundingAmountTo = useDebounce(fundingAmountTo, 500)
  const [postValuationFrom, setPostValuationFrom] = useState<number | null>(
    props.initialCondition.postMoneyFrom ?? null
  )
  const [postValuationTo, setPostValuationTo] = useState<number | null>(
    props.initialCondition.postMoneyTo ?? null
  )
  const debouncedPostValuationFrom = useDebounce(postValuationFrom, 500)
  const debouncedPostValuationTo = useDebounce(postValuationTo, 500)
  const [preValuationFrom, setPreValuationFrom] = useState<number | null>(null)
  const [preValuationTo, setPreValuationTo] = useState<number | null>(null)
  const debouncedPreValuationFrom = useDebounce(preValuationFrom, 500)
  const debouncedPreValuationTo = useDebounce(preValuationTo, 500)
  const [term, setTerm] = useState<Term>({
    from: props.initialCondition.from ?? null,
    to: props.initialCondition.to ?? null,
  })

  useEffect(() => {
    setLoading(true)
    const { keywords, tags } = extractTagsAndKeywords(searchCondition.keywords)
    if (props.view === 'series') {
      // NOTE: 調達額フィルタはバグっているので暫定対応で「シリーズ別」モードのときは条件から外す
      const filteredSearchCondition = { ...searchCondition }
      delete filteredSearchCondition.fundingAmountFrom
      delete filteredSearchCondition.fundingAmountTo

      getRoundsDebounced({ ...filteredSearchCondition, keywords, tags })
        .then((res) => {
          props.setRounds(res.data)
          props.setCount(res.count)
          setLoading(false)
        })
        .catch((e) => {
          if (e !== ABORTED) {
            printError(e)
            setLoading(false)
          }
        })
    } else {
      getFundingRoundsDebounced({ ...searchCondition, keywords, tags })
        .then((res) => {
          props.setFundingRounds(res.data)
          props.setCount(res.count)
          props.setSearchConditions({ ...searchCondition, keywords, tags })
          setLoading(false)
        })
        .catch((e) => {
          if (e !== ABORTED) {
            printError(e)
            setLoading(false)
          }
        })
    }
  }, [searchCondition])

  useEffect(() => {
    setSearchCondition({
      ...searchCondition,
      page: props.page,
      offset: (props.page - 1) * ITEM_PER_PAGE,
    })
  }, [props.page])

  useEffect(() => {
    props.setPage(1)
    setSearchCondition({
      ...searchCondition,
      page: 1,
      offset: 0,
      sort: props.sort,
      order: props.order,
      keywords: debouncedKeywords,
      initialSeries: selectedInitialSeries,
      fundingAmountFrom: debouncedFundingAmountFrom || undefined,
      fundingAmountTo: debouncedFundingAmountTo || undefined,
      postValuationFrom: debouncedPostValuationFrom || undefined,
      postValuationTo: debouncedPostValuationTo || undefined,
      preValuationFrom: debouncedPreValuationFrom || undefined,
      preValuationTo: debouncedPreValuationTo || undefined,
      termFrom: term.from,
      termTo: term.to,
    })
  }, [
    props.sort,
    props.order,
    debouncedKeywords,
    selectedInitialSeries,
    debouncedFundingAmountFrom,
    debouncedFundingAmountTo,
    debouncedPostValuationFrom,
    debouncedPostValuationTo,
    debouncedPreValuationFrom,
    debouncedPreValuationTo,
    term,
    props.view,
  ])

  return (
    <>
      <KeywordBox
        keywords={keywords}
        setKeywords={setKeywords}
        className={'searchBox'}
      />
      <div>
        <Heading>フィルタ</Heading>
        <ul
          css={css`
            display: inline;
            vertical-align: middle;
            margin: 0 15px;
            padding: 0;
            list-style: none;
          `}
        >
          <InitialSeriesSelector
            selectedInitialSeries={selectedInitialSeries}
            setSelectedInitialSeries={setSelectedInitialSeries}
            opening={props.openingCondition === 'InitialSeries'}
            setOpeningCondition={props.setOpeningCondition}
          />
          <TermFilter
            term={term}
            setTerm={setTerm}
            opening={props.openingCondition === 'Term'}
            setOpeningCondition={props.setOpeningCondition}
          />
          {props.view !== 'series' ? (
            <FundingAmountFilter
              fundingAmountFrom={fundingAmountFrom}
              setFundingAmountFrom={setFundingAmountFrom}
              fundingAmountTo={fundingAmountTo}
              setFundingAmountTo={setFundingAmountTo}
              opening={props.openingCondition === 'FundingAmount'}
              setOpeningCondition={props.setOpeningCondition}
            />
          ) : null}
          <PreValuationFilter
            preValuationFrom={preValuationFrom}
            setPreValuationFrom={setPreValuationFrom}
            preValuationTo={preValuationTo}
            setPreValuationTo={setPreValuationTo}
            opening={props.openingCondition === 'PreValuation'}
            setOpeningCondition={props.setOpeningCondition}
          />
          <PostValuationFilter
            postValuationFrom={postValuationFrom}
            setPostValuationFrom={setPostValuationFrom}
            postValuationTo={postValuationTo}
            setPostValuationTo={setPostValuationTo}
            opening={props.openingCondition === 'PostValuation'}
            setOpeningCondition={props.setOpeningCondition}
          />
        </ul>
        <LoadingOverlay active={loading}></LoadingOverlay>
      </div>
      <div
        css={css`
          margin-top: 15px;
          display: flex;
        `}
      >
        <Heading>ビュー</Heading>
        <ViewModeSelector view={props.view} setView={props.setView} />
      </div>
    </>
  )
}

type HeadingProps = {
  children: React.ReactNode
}

const Heading: React.FC<HeadingProps> = (props) => {
  return (
    <div
      css={css`
        color: ${colors.textLight};
        display: inline-block;
        font-size: 14px;
        font-weight: ${font.weight.bold};
        vertical-align: middle;
        margin: 0;
      `}
    >
      {props.children}
    </div>
  )
}

export const extractTagsAndKeywords = (input: string) => {
  const tagRegexp = /(#[^\s]+)/g
  const tagList = input.match(tagRegexp) || []
  const tags = tagList.map((s) => s.replace('#', '')).join(',')
  const keywords = input.replace(tagRegexp, '')
  return { tags, keywords }
}
